import React from 'react';
import {
  Breakpoint,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '../Button/Button';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string | string[];
  confirmLabel?: string;
  cancelLabel?: string;
  showDivider?: boolean;
  disabled?: boolean;
  // Narrow it with 'xs' when the dialog opens over another dialog, so it does
  // not sit edge to edge with the modal underneath.
  maxWidth?: Breakpoint;
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  description = 'Do you want to proceed with this action?',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  showDivider = true,
  disabled = false,
  maxWidth = 'sm',
}) => {
  const getDescriptionText = (text: string) => (
    <Typography className='text-text-2 font-circular text-[16px] leading-[24px]'>
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={maxWidth}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
      sx={{ '& .MuiDialog-paper': { '@media (max-width: 599.95px)': { m: 1.5, width: 'calc(100% - 24px)' } } }}
    >
      <DialogTitle>
        <div className='flex justify-between items-center'>
          <Typography variant='h5' className='text-[20px]'>
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon className='text-text-2' />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className='pb-[24px] flex flex-col gap-2'>
          {Array.isArray(description)
            ? description.map((item, index) => (
                <React.Fragment key={index}>
                  {getDescriptionText(item)}
                </React.Fragment>
              ))
            : getDescriptionText(description)}
        </div>
        {showDivider && <hr className='border-t border-border-token' />}
      </DialogContent>

      <DialogActions className='!px-6 !py-4'>
        {cancelLabel && (
          <Button
            variant='outlined'
            onClick={onClose}
            disabled={disabled}
            className='h-[40px] flex items-center !py-0 !px-4 !min-w-fit'
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          variant='contained'
          onClick={onConfirm}
          disabled={disabled}
          className='h-[40px] flex items-center !py-0 !px-4 !min-w-fit'
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
